import React, { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useSession } from '../hooks/useSession';
import Header from './Header';
import Footer from './Footer';

interface LocationState {
  from?: { pathname?: string };
}

const SignInPage: React.FC = () => {
  const { session, isLoaded } = useSession();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const redirectTo = (location.state as LocationState | null)?.from?.pathname ?? '/my-list';

  if (isLoaded && session) {
    return <Navigate to={redirectTo} replace />;
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error: authError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    setSubmitting(false);
    if (authError) {
      setError(authError.message);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <form
          onSubmit={handleSubmit}
          className="card w-full max-w-md p-8 space-y-5"
          aria-labelledby="sign-in-heading"
        >
          <h1
            id="sign-in-heading"
            className="font-display text-3xl text-charcoal mb-1"
          >
            Sign in
          </h1>
          <p className="text-sm text-charcoal-light">
            Welcome back. Sign in to curate your top five.
          </p>

          <label className="block text-sm">
            <span className="text-charcoal-light">Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full mt-1 px-4 py-3 bg-cream-warm border border-black/10 rounded-lg text-charcoal focus:outline-none focus:border-amber"
            />
          </label>

          <label className="block text-sm">
            <span className="text-charcoal-light">Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full mt-1 px-4 py-3 bg-cream-warm border border-black/10 rounded-lg text-charcoal focus:outline-none focus:border-amber"
            />
          </label>

          <div className="-mt-2 text-right">
            <Link
              to="/forgot-password"
              className="text-xs text-charcoal-light hover:text-amber"
            >
              Forgot password?
            </Link>
          </div>

          {error && (
            <p className="text-sm text-terracotta" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full justify-center disabled:opacity-50"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="text-sm text-charcoal-light text-center">
            No account yet?{' '}
            <Link to="/sign-up" className="text-amber hover:underline">
              Sign up
            </Link>
          </p>
        </form>
      </main>
      <Footer />
    </div>
  );
};

export default SignInPage;
